import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './authConfig';
import App from './App';
import './styles/index.css';

// ── Error boundary — surfaces any unhandled render errors on screen ────────────

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          background: '#010E23', color: '#fff', padding: '2rem', fontFamily: 'monospace',
        }}>
          <h2 style={{ color: '#f87171', marginBottom: '1rem' }}>Application error</h2>
          <pre style={{
            background: '#1e293b', borderRadius: '8px', padding: '1rem',
            maxWidth: '640px', width: '100%', overflowX: 'auto',
            fontSize: '0.75rem', color: '#fca5a5',
          }}>
            {(error as Error).message}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1.5rem', padding: '0.5rem 1.5rem',
              background: '#0078d4', border: 'none', borderRadius: '8px',
              color: '#fff', cursor: 'pointer', fontSize: '0.875rem',
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── MSAL setup ─────────────────────────────────────────────────────────────────

export const msalInstance = new PublicClientApplication(msalConfig);
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

msalInstance.initialize().then(() => {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
});
